import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createFolder,
  deleteFile,
  deleteFolder,
  downloadFile,
  fetchContents,
  fetchFolderTree,
  renameFile,
  renameFolder,
  reorderFolders,
  searchFolders,
  uploadFile,
} from '../../api/boardContent';
import { useCompanyStore } from '../../store/companyStore';
import { canManageContracts } from '../../types/companyRole';
import {
  BoardItem,
  ContentSortDir,
  ContentSortKey,
  FolderTreeNode,
} from '../../types/boardContent';
import FolderTree from './FolderTree';
import MergePdfModal from './MergePdfModal';
import BoardFilePreviewModal from './BoardFilePreviewModal';
import {
  collectUploadNodesFromDataTransfer,
  collectUploadNodesFromFileList,
  countUploadNodes,
  UploadNode,
} from './folderDropUpload';
import './BoardExplorer.css';

import { reportError } from '../../utils/notify';
interface BoardExplorerProps {
  boardId: number;
  boardName: string;
  /** 있으면 해당 계약에 첨부된 파일만 조회·업로드 (단계 탭 임베드용) */
  contractId?: number | null;
}

function collectExpandPath(
  nodes: FolderTreeNode[],
  targetId: number,
  path: number[] = []
): number[] | null {
  for (const node of nodes) {
    const next = [...path, node.folderId];
    if (node.folderId === targetId) {
      return next;
    }
    const found = collectExpandPath(node.children || [], targetId, next);
    if (found) return found;
  }
  return null;
}

function BoardExplorer({ boardId, boardName, contractId = null }: BoardExplorerProps) {
  const role = useCompanyStore((state) => state.role);
  const canMergePdf = canManageContracts(role) && contractId == null;
  const contractScoped = contractId != null;
  const [tree, setTree] = useState<FolderTreeNode[]>([]);
  const [items, setItems] = useState<BoardItem[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchResults, setSearchResults] = useState<FolderTreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [fileDropActive, setFileDropActive] = useState(false);
  const fileDropDepth = useRef(0);
  const [sortKey, setSortKey] = useState<ContentSortKey>('default');
  const [sortDir, setSortDir] = useState<ContentSortDir>('asc');
  const [draggingFolderId, setDraggingFolderId] = useState<number | null>(null);
  const [dragOverFolderId, setDragOverFolderId] = useState<number | null>(null);
  const [reordering, setReordering] = useState(false);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeNotice, setMergeNotice] = useState('');
  const [previewFile, setPreviewFile] = useState<BoardItem | null>(null);

  const refresh = useCallback(
    async (folderId: number | null = currentFolderId) => {
      try {
        setLoading(true);
        const [treeData, contentData] = await Promise.all([
          fetchFolderTree(boardId),
          fetchContents(boardId, folderId, contractId),
        ]);
        setTree(treeData);
        setItems(contentData);
        setError('');
      } catch {
        reportError(setError, '문서함을 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    },
    [boardId, contractId, currentFolderId]
  );

  useEffect(() => {
    setCurrentFolderId(null);
    setExpandedIds(new Set());
    setSearchKeyword('');
    setSearchResults([]);
    setSortKey('default');
    setSortDir('asc');
    refresh(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId, contractId]);

  useEffect(() => {
    const keyword = searchKeyword.trim();
    if (keyword.length < 1) {
      setSearchResults([]);
      return;
    }
    const timer = window.setTimeout(async () => {
      try {
        const results = await searchFolders(boardId, keyword);
        setSearchResults(results);
      } catch {
        setSearchResults([]);
      }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [searchKeyword, boardId]);

  const currentTitle = useMemo(() => {
    if (currentFolderId == null) return '루트';
    const path = collectExpandPath(tree, currentFolderId);
    if (!path) return '폴더';
    const names: string[] = [];
    let nodes = tree;
    for (const id of path) {
      const node = nodes.find((n) => n.folderId === id);
      if (!node) break;
      names.push(node.folderName);
      nodes = node.children || [];
    }
    return names.join(' / ');
  }, [currentFolderId, tree]);

  const rootFolders = useMemo(
    () => tree.filter((node) => node.parentFolderId == null),
    [tree]
  );

  const sortedItems = useMemo(() => {
    if (sortKey === 'default') {
      return items;
    }

    const copied = [...items];
    const direction = sortDir === 'asc' ? 1 : -1;
    copied.sort((a, b) => {
      if (sortKey === 'name') {
        return a.name.localeCompare(b.name, 'ko') * direction;
      }
      const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return (aTime - bTime) * direction;
    });
    return copied;
  }, [items, sortKey, sortDir]);

  const handleSelectFolder = (folderId: number | null) => {
    setCurrentFolderId(folderId);
    if (folderId != null) {
      const path = collectExpandPath(tree, folderId) || [];
      setExpandedIds((prev) => {
        const next = new Set(prev);
        path.forEach((id) => next.add(id));
        return next;
      });
    }
    refresh(folderId);
  };

  const handleToggle = (folderId: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  const handleCreateFolder = async (event: FormEvent) => {
    event.preventDefault();
    const name = newFolderName.trim();
    if (!name) return;
    try {
      setCreating(true);
      await createFolder(boardId, name, currentFolderId);
      setNewFolderName('');
      await refresh(currentFolderId);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '폴더 생성에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setCreating(false);
    }
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    event.target.value = '';
    if (!files?.length) return;
    await uploadNodes(
      collectUploadNodesFromFileList(files),
      currentFolderId
    );
  };

  const handleFolderPick = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    event.target.value = '';
    if (!files?.length) return;
    await uploadNodes(
      collectUploadNodesFromFileList(files),
      currentFolderId
    );
  };

  const uploadNodes = async (
    nodes: UploadNode[],
    parentFolderId: number | null
  ) => {
    if (nodes.length === 0) return;
    const { folders, files } = countUploadNodes(nodes);
    try {
      setUploading(true);
      setError('');
      setUploadProgress(
        folders > 0
          ? `폴더 ${folders}개 · 파일 ${files}개 업로드 중…`
          : `파일 ${files}개 업로드 중…`
      );

      const uploadOne = async (node: UploadNode, parentId: number | null) => {
        if (node.type === 'file') {
          setUploadProgress(`업로드 중… ${node.name}`);
          await uploadFile(boardId, node.file, parentId, contractId);
          return;
        }
        setUploadProgress(`폴더 생성… ${node.name}`);
        const created = await createFolder(boardId, node.name, parentId);
        for (const child of node.children) {
          await uploadOne(child, created.id);
        }
      };

      for (const node of nodes) {
        await uploadOne(node, parentFolderId);
      }
      await refresh(currentFolderId);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? '업로드에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setUploading(false);
      setUploadProgress('');
    }
  };

  const isOsFileDrag = (event: React.DragEvent) => {
    if (draggingFolderId != null) return false;
    return Array.from(event.dataTransfer?.types || []).includes('Files');
  };

  const handleOsDragEnter = (event: React.DragEvent) => {
    if (!isOsFileDrag(event)) return;
    event.preventDefault();
    fileDropDepth.current += 1;
    setFileDropActive(true);
  };

  const handleOsDragLeave = (event: React.DragEvent) => {
    if (!isOsFileDrag(event) && !fileDropActive) return;
    event.preventDefault();
    fileDropDepth.current = Math.max(0, fileDropDepth.current - 1);
    if (fileDropDepth.current === 0) setFileDropActive(false);
  };

  const handleOsDragOver = (event: React.DragEvent) => {
    if (!isOsFileDrag(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleOsDrop = async (
    event: React.DragEvent,
    targetFolderId: number | null = currentFolderId
  ) => {
    if (!isOsFileDrag(event)) return;
    event.preventDefault();
    event.stopPropagation();
    fileDropDepth.current = 0;
    setFileDropActive(false);
    setDragOverFolderId(null);
    try {
      const nodes = await collectUploadNodesFromDataTransfer(event.dataTransfer);
      await uploadNodes(nodes, targetFolderId);
    } catch {
      reportError(setError, '드래그한 항목을 읽지 못했습니다.');
    }
  };

  const handleRenameItem = async (item: BoardItem) => {
    const nextName = window.prompt('새 이름을 입력하세요.', item.name);
    if (nextName == null) return;
    const name = nextName.trim();
    if (!name || name === item.name) return;

    try {
      if (item.type === 'FOLDER') {
        await renameFolder(boardId, item.id, name);
      } else {
        await renameFile(boardId, item.id, name);
      }
      await refresh(currentFolderId);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '이름 변경에 실패했습니다.';
      reportError(setError, message);
    }
  };

  const handleReorderFolders = async (orderedFolderIds: number[]) => {
    try {
      setReordering(true);
      setSortKey('default');
      await reorderFolders(boardId, orderedFolderIds);
      await refresh(currentFolderId);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '폴더 순서 변경에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setReordering(false);
      setDraggingFolderId(null);
      setDragOverFolderId(null);
    }
  };

  const handleFolderDrop = async (targetFolderId: number) => {
    if (sortKey !== 'default' || draggingFolderId == null || draggingFolderId === targetFolderId) {
      setDraggingFolderId(null);
      setDragOverFolderId(null);
      return;
    }

    const folders = items.filter((item) => item.type === 'FOLDER');
    const fromIndex = folders.findIndex((folder) => folder.id === draggingFolderId);
    const toIndex = folders.findIndex((folder) => folder.id === targetFolderId);
    if (fromIndex < 0 || toIndex < 0) {
      setDraggingFolderId(null);
      setDragOverFolderId(null);
      return;
    }

    const nextFolders = [...folders];
    const [moved] = nextFolders.splice(fromIndex, 1);
    nextFolders.splice(toIndex, 0, moved);
    await handleReorderFolders(nextFolders.map((folder) => folder.id));
  };

  const handleDeleteItem = async (item: BoardItem) => {
    const ok =
      item.type === 'FOLDER'
        ? window.confirm(
            `'${item.name}' 폴더를 삭제할까요?\n\n하위 폴더와 파일이 모두 함께 삭제되며, 되돌릴 수 없습니다.`
          )
        : window.confirm(`'${item.name}'을(를) 삭제할까요?`);
    if (!ok) return;
    try {
      if (item.type === 'FOLDER') {
        await deleteFolder(boardId, item.id);
        if (currentFolderId === item.id) {
          setCurrentFolderId(item.parentFolderId ?? null);
          await refresh(item.parentFolderId ?? null);
          return;
        }
      } else {
        await deleteFile(boardId, item.id);
      }
      await refresh(currentFolderId);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '삭제에 실패했습니다.';
      reportError(setError, message);
    }
  };

  const handleSortChange = (key: ContentSortKey) => {
    if (key === sortKey && key !== 'default') {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'updatedAt' ? 'desc' : 'asc');
  };

  return (
    <main className={`board-explorer${contractScoped ? ' board-explorer--contract' : ''}`}>
      <div className="board-explorer__sidebar">
        <div className="board-explorer__side-header">
          <strong>{boardName}</strong>
          <span>{contractScoped ? '이 계약 첨부 문서' : '폴더 구조'}</span>
        </div>

        <div className="board-explorer__search">
          <input
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            placeholder="폴더명 검색"
          />
          {searchResults.length > 0 && (
            <ul className="board-explorer__search-list">
              {searchResults.map((item) => (
                <li key={item.folderId}>
                  <button
                    type="button"
                    onClick={() => {
                      handleSelectFolder(item.folderId);
                      setSearchKeyword('');
                      setSearchResults([]);
                    }}
                  >
                    {item.folderName}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="board-explorer__tree-wrap">
          <FolderTree
            nodes={tree}
            selectedFolderId={currentFolderId}
            expandedIds={expandedIds}
            onToggle={handleToggle}
            onSelect={handleSelectFolder}
          />
        </div>
      </div>

      <div className="board-explorer__main">
        <div className="board-explorer__toolbar">
          <div>
            <h2>{boardName}</h2>
            <p>
              {contractScoped
                ? `${currentTitle} · 이 계약서에 첨부된 파일만 표시됩니다`
                : currentTitle}
            </p>
          </div>
          <div className="board-explorer__actions">
            <form className="board-explorer__create" onSubmit={handleCreateFolder}>
              <input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="새 폴더명"
                maxLength={100}
              />
              <button type="submit" disabled={creating}>
                폴더 추가
              </button>
            </form>
            <label className="board-explorer__upload">
              {uploading ? '업로드 중...' : '문서 등록'}
              <input
                type="file"
                multiple
                onChange={(e) => void handleUpload(e)}
                hidden
                disabled={uploading}
              />
            </label>
            <label
              className="board-explorer__upload is-folder"
              title="PC 폴더를 선택하면 하위 구조까지 그대로 등록됩니다"
            >
              {uploading ? '업로드 중...' : '폴더 등록'}
              <input
                type="file"
                multiple
                // Chromium/Safari: 폴더 선택
                {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
                onChange={(e) => void handleFolderPick(e)}
                hidden
                disabled={uploading}
              />
            </label>
          </div>
        </div>

        {uploadProgress ? (
          <p className="board-explorer__notice">{uploadProgress}</p>
        ) : null}

        <div className="board-explorer__sortbar">
          <span>정렬</span>
          <button
            type="button"
            className={sortKey === 'default' ? 'is-active' : ''}
            onClick={() => handleSortChange('default')}
          >
            폴더순서
          </button>
          <button
            type="button"
            className={sortKey === 'name' ? 'is-active' : ''}
            onClick={() => handleSortChange('name')}
          >
            이름{sortKey === 'name' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
          </button>
          <button
            type="button"
            className={sortKey === 'updatedAt' ? 'is-active' : ''}
            onClick={() => handleSortChange('updatedAt')}
          >
            수정일{sortKey === 'updatedAt' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
          </button>
          {sortKey === 'default' ? (
            <em>폴더 행 드래그=순서 변경 · PC에서 폴더/파일 드롭=업로드</em>
          ) : (
            <em>폴더 드래그 정렬은 &apos;폴더순서&apos;에서만 가능합니다.</em>
          )}
          {canMergePdf && (
            <button
              type="button"
              className="board-explorer__merge-pdf"
              onClick={() => {
                setMergeNotice('');
                setMergeModalOpen(true);
              }}
            >
              통합 PDF
            </button>
          )}
        </div>

        {mergeNotice && <p className="board-explorer__notice">{mergeNotice}</p>}

        {error && <p className="board-explorer__error">{error}</p>}

        <div
          className={`board-explorer__content${fileDropActive ? ' is-file-drop' : ''}`}
          onDragEnter={handleOsDragEnter}
          onDragLeave={handleOsDragLeave}
          onDragOver={handleOsDragOver}
          onDrop={(event) => void handleOsDrop(event, currentFolderId)}
        >
          {fileDropActive ? (
            <div className="board-explorer__drop-hint" aria-hidden="true">
              폴더·파일을 여기에 놓으면 현재 위치에 업로드됩니다
            </div>
          ) : null}
          {loading ? (
            <p className="board-explorer__hint">불러오는 중...</p>
          ) : sortedItems.length === 0 ? (
            <p className="board-explorer__hint">
              폴더나 문서를 추가해 주세요. 폴더째로 드래그하거나 「폴더 등록」으로도
              올릴 수 있습니다.
            </p>
          ) : (
            <table className="ec-table board-explorer__table">
              <thead>
                <tr>
                  <th style={{ width: '36px' }} />
                  <th style={{ width: '64px' }}>유형</th>
                  <th>이름</th>
                  <th style={{ width: '90px' }}>크기</th>
                  <th style={{ width: '140px' }}>수정일</th>
                  <th style={{ width: '100px' }}>수정자</th>
                  <th style={{ width: '220px' }}>작업</th>
                </tr>
              </thead>
              <tbody>
                {sortedItems.map((item) => {
                  const isFolder = item.type === 'FOLDER';
                  const canDrag = isFolder && sortKey === 'default' && !reordering;
                  const isDragging = draggingFolderId === item.id;
                  const isDragOver = dragOverFolderId === item.id && draggingFolderId !== item.id;

                  return (
                    <tr
                      key={`${item.type}-${item.id}`}
                      className={[
                        canDrag ? 'is-draggable' : '',
                        isDragging ? 'is-dragging' : '',
                        isDragOver ? 'is-drag-over' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onDragOver={(event) => {
                        if (isOsFileDrag(event)) {
                          event.preventDefault();
                          event.stopPropagation();
                          event.dataTransfer.dropEffect = 'copy';
                          if (isFolder) setDragOverFolderId(item.id);
                          return;
                        }
                        if (!isFolder || sortKey !== 'default' || draggingFolderId == null) return;
                        event.preventDefault();
                        event.dataTransfer.dropEffect = 'move';
                        setDragOverFolderId(item.id);
                      }}
                      onDragLeave={() => {
                        if (dragOverFolderId === item.id) {
                          setDragOverFolderId(null);
                        }
                      }}
                      onDrop={(event) => {
                        if (isOsFileDrag(event)) {
                          event.preventDefault();
                          event.stopPropagation();
                          void handleOsDrop(event, isFolder ? item.id : currentFolderId);
                          return;
                        }
                        if (!isFolder || sortKey !== 'default') return;
                        event.preventDefault();
                        void handleFolderDrop(item.id);
                      }}
                    >
                      <td className="board-explorer__drag">
                        {isFolder ? (
                          <span
                            className="board-explorer__drag-handle"
                            title={canDrag ? '드래그하여 순서 변경' : '폴더순서 정렬에서 드래그 가능'}
                            draggable={canDrag}
                            onDragStart={(event) => {
                              if (!canDrag) {
                                event.preventDefault();
                                return;
                              }
                              setDraggingFolderId(item.id);
                              event.dataTransfer.effectAllowed = 'move';
                              event.dataTransfer.setData('text/plain', String(item.id));
                            }}
                            onDragEnd={() => {
                              setDraggingFolderId(null);
                              setDragOverFolderId(null);
                            }}
                          >
                            ⋮⋮
                          </span>
                        ) : null}
                      </td>
                      <td>{isFolder ? '폴더' : (item.ext || '파일').toUpperCase()}</td>
                      <td>
                        {isFolder ? (
                          <button
                            type="button"
                            className="board-explorer__name-hit"
                            onClick={() => handleSelectFolder(item.id)}
                          >
                            <span className="board-explorer__name-icon" aria-hidden="true">
                              📂
                            </span>
                            <span className="board-explorer__name-text">{item.name}</span>
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="board-explorer__name-hit"
                            onClick={() => setPreviewFile(item)}
                            title="미리보기"
                          >
                            <span className="board-explorer__name-icon" aria-hidden="true">
                              📄
                            </span>
                            <span className="board-explorer__name-text">{item.name}</span>
                          </button>
                        )}
                      </td>
                      <td>{item.type === 'FILE' ? formatSize(item.size) : '-'}</td>
                      <td>{formatDateTime(item.updatedAt)}</td>
                      <td>{formatUpdaterName(item)}</td>
                      <td>
                        <div className="board-explorer__row-actions">
                          <button type="button" onClick={() => handleRenameItem(item)}>
                            이름변경
                          </button>
                          {item.type === 'FILE' && (
                            <>
                              <button type="button" onClick={() => setPreviewFile(item)}>
                                미리보기
                              </button>
                              <button
                                type="button"
                                onClick={() => downloadFile(boardId, item.id, item.name)}
                              >
                                다운로드
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            className="is-danger"
                            onClick={() => handleDeleteItem(item)}
                          >
                            삭제
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {previewFile ? (
        <BoardFilePreviewModal
          boardId={boardId}
          file={previewFile}
          onClose={() => setPreviewFile(null)}
        />
      ) : null}

      {mergeModalOpen && (
        <MergePdfModal
          boardId={boardId}
          rootFolders={rootFolders}
          onClose={() => setMergeModalOpen(false)}
          onDone={async (created) => {
            setMergeModalOpen(false);
            setMergeNotice(`통합 PDF가 저장되었습니다: ${created.name}`);
            setCurrentFolderId(null);
            await refresh(null);
          }}
        />
      )}
    </main>
  );
}

function formatSize(size?: number | null) {
  if (size == null) return '-';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}

/** 수정자 컬럼에는 PK가 아니라 사용자명만 표시 */
function formatUpdaterName(item: BoardItem) {
  const name = item.lastUpdatedUserName?.trim();
  return name ? name : '-';
}

export default BoardExplorer;
